import type { QueryClient } from '@tanstack/react-query';
import { fetchNewestPrograms, fetchProgramById } from '@/hooks/usePrograms';
import { fetchEventById } from '@/hooks/useEvents';
import { getHeroImageUrl } from '@/utils/imageUtils';
import { shouldPrefetch } from '@/routes/prefetchRoutes';
import { programKeys } from '@/lib/queryKeys';

const prefetchedData = new Set<string>();
const prefetchedImages = new Set<string>();

function makeKey(type: string, id: string) {
  return `${type}:${id}`;
}

function preloadImage(url: string | null | undefined) {
  if (!url) return;
  if (typeof window === 'undefined' || typeof Image === 'undefined') return;
  if (prefetchedImages.has(url)) return;

  prefetchedImages.add(url);
  const img = new Image();
  img.decoding = 'async';
  (img as HTMLImageElement & { fetchPriority?: 'high' }).fetchPriority = 'high';
  img.src = url;

  if (typeof img.decode === 'function') {
    void img.decode().catch(() => {
      // Ignore decode failures; fetch still warms browser cache.
    });
  }
}

export function prefetchProgramData(queryClient: QueryClient, programId: string | undefined) {
  if (!import.meta.env.PROD) return;
  if (!shouldPrefetch()) return;
  if (!programId) return;

  const queryKey = programKeys.detail(programId);
  const cacheHit = queryClient.getQueryData(queryKey);
  const key = makeKey('program', programId);

  if (cacheHit || prefetchedData.has(key)) return;

  prefetchedData.add(key);
  const promise = queryClient.prefetchQuery({
    queryKey,
    queryFn: () => fetchProgramById(programId),
    staleTime: 2 * 60 * 1000,
  });

  promise.catch(() => {
    prefetchedData.delete(key);
  });

  return promise;
}

export function prefetchHomeDashboardData(queryClient: QueryClient, userId: string | undefined) {
  if (!import.meta.env.PROD) return;
  if (!shouldPrefetch()) return;

  const key = makeKey('home-dashboard', userId || 'guest');
  if (prefetchedData.has(key)) return;

  prefetchedData.add(key);
  const queryKey = programKeys.newest(10, userId, null);
  const promise = queryClient.fetchQuery({
    queryKey,
    queryFn: () => fetchNewestPrograms(10),
    staleTime: 5 * 60 * 1000,
  }).then((programs) => {
    const heroProgram = programs.find((program) => Boolean(program.thumbnail_url));
    preloadImage(getHeroImageUrl(heroProgram?.thumbnail_url));
  });

  promise.catch(() => {
    prefetchedData.delete(key);
  });

  return promise;
}

interface EventSessionPrefetchOptions {
  /**
   * Optional known program ID to skip waiting for event details.
   * Useful when caller already has event/program shape from a list payload.
   */
  programId?: string | null;
}

/**
 * Prefetches event landing essentials:
 * - Event detail
 * - Linked program (for prompt timeline)
 */
export function prefetchEventSessionData(
  queryClient: QueryClient,
  eventId: string | undefined,
  options: EventSessionPrefetchOptions = {}
) {
  if (!import.meta.env.PROD) return;
  if (!shouldPrefetch()) return;
  if (!eventId) return;

  const key = makeKey('event-session', eventId);
  if (prefetchedData.has(key)) return;

  prefetchedData.add(key);
  const promise = queryClient.fetchQuery({
    queryKey: ['event', eventId],
    queryFn: () => fetchEventById(eventId),
    staleTime: 30 * 1000,
  }).then(async (eventData) => {
    const programId = options.programId ?? eventData?.program_id ?? null;
    if (programId) {
      await queryClient.prefetchQuery({
        queryKey: programKeys.detail(programId),
        queryFn: () => fetchProgramById(programId),
        staleTime: 2 * 60 * 1000,
      });
    }
  });

  promise.catch(() => {
    prefetchedData.delete(key);
  });

  return promise;
}

